import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const CanjesDenarios = ({ user }) => {
  const API = `${window.location.origin}/api`;
  const [planes, setPlanes] = useState([]);
  const [saldoActual, setSaldoActual] = useState(0);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [claseSeleccionada, setClaseSeleccionada] = useState('');
  const [mensajeAdicional, setMensajeAdicional] = useState(''); // texto opcional del estudiante (ej: qué prueba quiere abrir)
  const [clases, setClases] = useState([]);
  const [activeTab, setActiveTab] = useState('disponibles'); // 'disponibles' o 'historial'

  useEffect(() => {
    loadPlanesDisponibles();
    loadHistorial();
  }, []);

  const loadPlanesDisponibles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/planes-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanes(response.data.planes || []);
      setSaldoActual(response.data.saldo_actual || 0);
    } catch (error) {
      console.error('Error loading planes:', error);
      toast.error('Error al cargar planes disponibles');
    } finally {
      setLoading(false);
    }
  };

  const loadHistorial = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/canjes/mi-historial`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistorial(response.data.canjes || []);
    } catch (error) {
      console.error('Error loading historial:', error);
    }
  };

  const loadClases = async (planParam = null) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/clases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clasesData = Array.isArray(response.data) ? response.data : [];
      // Filtrar solo clases donde el estudiante está inscrito
      let clasesInscritas = clasesData.filter(clase => 
        clase.estudiantes_inscritos && clase.estudiantes_inscritos.includes(user.id)
      );
      // Si el plan tiene clases_permitidas configuradas, filtrar solo a esas
      const planRef = planParam || planSeleccionado;
      if (planRef?.clases_permitidas?.length > 0) {
        const permitidas = new Set(planRef.clases_permitidas);
        clasesInscritas = clasesInscritas.filter(c => permitidas.has(c.id));
      }
      // Ordenar alfabéticamente por título
      clasesInscritas.sort((a, b) =>
        (a.titulo || a.nombre || '').localeCompare(b.titulo || b.nombre || '', 'es', { sensitivity: 'base' })
      );
      setClases(clasesInscritas);
    } catch (error) {
      console.error('Error loading clases:', error);
    }
  };

  const openConfirmModal = async (plan) => {
    setPlanSeleccionado(plan);
    setClaseSeleccionada('');
    setMensajeAdicional('');
    
    if (plan.requiere_seleccion_clase) {
      await loadClases(plan);
    }
    
    setShowConfirmModal(true);
  };

  const handleCanjear = async () => {
    if (!planSeleccionado) return;

    // Validar selección de clase si es requerida
    if (planSeleccionado.requiere_seleccion_clase && !claseSeleccionada) {
      toast.error('Debes seleccionar una clase');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/canjes/canjear`,
        {
          plan_id: planSeleccionado.id,
          clase_seleccionada_id: claseSeleccionada || null,
          mensaje_estudiante: (mensajeAdicional || '').trim() || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message, {
        autoClose: 5000,
        style: {
          background: 'linear-gradient(to right, #10b981, #3b82f6)',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold'
        }
      });

      setShowConfirmModal(false);
      setPlanSeleccionado(null);
      setClaseSeleccionada('');
      setMensajeAdicional('');
      loadPlanesDisponibles();
      loadHistorial();
    } catch (error) {
      console.error('Error canjeando:', error);
      toast.error(error.response?.data?.detail || 'Error al realizar canje');
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'N/A';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-UY', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoriaColor = (categoria) => {
    const colors = {
      'Académico': 'bg-blue-100 text-blue-700 border-blue-200',
      'Material': 'bg-green-100 text-green-700 border-green-200',
      'Certificados': 'bg-purple-100 text-purple-700 border-purple-200',
      'Privilegios': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Otros': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[categoria] || colors['Otros'];
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-8">
      {/* Header con Saldo */}
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 flex items-center">
          <span className="text-3xl sm:text-4xl mr-3">🎁</span>
          Canje de Denarios
        </h2>
        
        {/* Saldo Card */}
        <div className="bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-2xl p-6 shadow-lg mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium mb-1">Tu Saldo Actual</p>
              <p className="text-white text-4xl font-bold">{saldoActual} ₯</p>
            </div>
            <div className="text-6xl">🪙</div>
          </div>
        </div>

        <p className="text-sm sm:text-base text-gray-600">
          Canjea tus denarios por beneficios exclusivos
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('disponibles')}
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'disponibles'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🛍️ Planes Disponibles
        </button>
        <button
          onClick={() => setActiveTab('historial')}
          className={`px-4 py-2 font-semibold transition-all ${
            activeTab === 'historial'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📜 Mi Historial ({historial.length})
        </button>
      </div>

      {/* Tab: Planes Disponibles */}
      {activeTab === 'disponibles' && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="text-xl text-gray-600">Cargando planes...</div>
            </div>
          ) : planes.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎁</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No hay planes disponibles en este momento
              </h3>
              <p className="text-gray-500">
                Los administradores pronto agregarán nuevos premios
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {planes.map(plan => (
                <div
                  key={plan.id}
                  className={`border-2 rounded-2xl p-6 transition-all hover:shadow-xl ${
                    plan.puede_canjear
                      ? 'border-indigo-200 bg-gradient-to-br from-white to-indigo-50'
                      : 'border-gray-200 bg-gray-50 opacity-75'
                  }`}
                >
                  {/* Icono */}
                  <div className="text-center mb-4">
                    <div className="text-6xl mb-2">{plan.icono}</div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getCategoriaColor(plan.categoria)}`}>
                      {plan.categoria}
                    </span>
                  </div>

                  {/* Nombre */}
                  <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                    {plan.nombre}
                  </h3>

                  {/* Descripción */}
                  <p className="text-sm text-gray-600 mb-4 text-center min-h-[40px]">
                    {plan.descripcion}
                  </p>

                  {/* Beneficio Destacado */}
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                    <p className="text-xs text-gray-600 font-semibold mb-1">✨ Beneficio:</p>
                    <p className="text-sm text-gray-800 font-medium">{plan.beneficio}</p>
                  </div>

                  {/* Costo */}
                  <div className="bg-white rounded-xl p-4 mb-4 shadow-inner">
                    <div className="text-center">
                      <p className="text-xs text-gray-500 mb-1">Costo</p>
                      <p className="text-3xl font-bold text-indigo-600">{plan.costo_denarios} ₯</p>
                    </div>
                  </div>

                  {/* Indicador de selección de clase */}
                  {plan.requiere_seleccion_clase && (
                    <div className="mb-4 text-center">
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                        📚 Requiere elegir clase
                      </span>
                    </div>
                  )}

                  {/* Botón Canjear */}
                  <button
                    onClick={() => openConfirmModal(plan)}
                    disabled={!plan.puede_canjear}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      plan.puede_canjear
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:shadow-lg hover:scale-105'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {plan.puede_canjear ? '🎉 Canjear Ahora' : '❌ Saldo Insuficiente'}
                  </button>

                  {!plan.puede_canjear && (
                    <p className="text-xs text-center text-red-600 mt-2">
                      Te faltan {plan.costo_denarios - saldoActual} ₯
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Tab: Historial */}
      {activeTab === 'historial' && (
        <>
          {historial.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📜</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Aún no has realizado canjes
              </h3>
              <p className="text-gray-500">
                Tus canjes aparecerán aquí cuando realices tu primer canje
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {historial.map(canje => (
                <div
                  key={canje.id}
                  className="border-2 border-gray-200 rounded-xl p-6 hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 mb-1">
                        {canje.plan_nombre}
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">{canje.plan_beneficio}</p>
                      {canje.clase_seleccionada_nombre && (
                        <p className="text-sm text-blue-600">
                          📚 Clase: {canje.clase_seleccionada_nombre}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600 mb-1">
                        -{canje.costo_denarios} ₯
                      </p>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                        {canje.estado}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    Canjeado el {formatFecha(canje.fecha_canje)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal de Confirmación */}
      {showConfirmModal && planSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-t-2xl">
              <h3 className="text-2xl font-bold text-white text-center">
                🎁 Confirmar Canje
              </h3>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{planSeleccionado.icono}</div>
                <h4 className="text-xl font-bold text-gray-800 mb-2">
                  {planSeleccionado.nombre}
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  {planSeleccionado.descripcion}
                </p>
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-800 font-medium">
                    ✨ {planSeleccionado.beneficio}
                  </p>
                </div>
              </div>

              {/* Selección de Clase */}
              {planSeleccionado.requiere_seleccion_clase && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Selecciona la clase *
                  </label>
                  <select
                    value={claseSeleccionada}
                    onChange={(e) => setClaseSeleccionada(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                    required
                  >
                    <option value="">-- Elige una clase --</option>
                    {clases.map(clase => (
                      <option key={clase.id} value={clase.id}>
                        {clase.titulo}
                      </option>
                    ))}
                  </select>
                  {clases.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      No estás inscrito en ninguna clase{planSeleccionado.clases_permitidas?.length > 0 ? ' habilitada para este canje' : ''}
                    </p>
                  )}
                </div>
              )}

              {/* Mensaje opcional del estudiante (ej: qué prueba/examen quiere) */}
              {planSeleccionado.requiere_seleccion_clase && (
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">
                    Mensaje al profesor <span className="text-gray-400 text-xs font-normal">(opcional)</span>
                  </label>
                  <textarea
                    data-testid="textarea-mensaje-canje"
                    value={mensajeAdicional}
                    onChange={(e) => setMensajeAdicional(e.target.value)}
                    placeholder="Ej: Quisiera que se reabra la Prueba de Hermenéutica del módulo 2"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {mensajeAdicional.length}/500 caracteres. Especificá detalles para que tu profesor sepa exactamente qué necesitás.
                  </p>
                </div>
              )}

              {/* Info de Costo */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Costo del canje:</span>
                  <span className="text-xl font-bold text-red-600">
                    -{planSeleccionado.costo_denarios} ₯
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Saldo actual:</span>
                  <span className="text-xl font-bold text-gray-800">{saldoActual} ₯</span>
                </div>
                <div className="border-t border-gray-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-800 font-semibold">Saldo después:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {saldoActual - planSeleccionado.costo_denarios} ₯
                    </span>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPlanSeleccionado(null);
                    setClaseSeleccionada('');
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCanjear}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                >
                  Confirmar Canje
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CanjesDenarios;
